import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  SPECIALISATIONS, SPECIALISATION_SHORT_LABELS,
  YEARS_OF_STUDY, YEAR_OF_STUDY_LABELS,
  AVAILABILITIES, AVAILABILITY_LABELS,
  type Specialisation, type YearOfStudy, type Availability,
} from "@intern-seek/shared";
import { api } from "../lib/api";
import {
  PageHeading, Alert, Spinner, Empty, SkillTags, SkillPicker, PillMultiFilter, Modal,
  SpecialisationBadge, YearBadge, Badge, SPECIALISATION_STYLES, YEAR_STYLES, FILTER_PILL, FieldPanel,
} from "../components/ui";

interface DirectoryStudent {
  id: string;
  firstName: string;
  lastName: string;
  specialisation: Specialisation;
  yearOfStudy: YearOfStudy;
  submittedAt: string;
  skills: string[];
  hobbies: string[];
  achievements: string[];
  /** Both null for students who submitted before these fields existed. */
  availability: Availability | null;
  aboutMe: string | null;
}

type SortKey = "name" | "specialisation" | "year";

const SPECIALISATION_OPTIONS = SPECIALISATIONS.map((s) => ({ value: s, label: SPECIALISATION_SHORT_LABELS[s] }));
const YEAR_OPTIONS = YEARS_OF_STUDY.map((y) => ({ value: y, label: YEAR_OF_STUDY_LABELS[y] }));
const AVAILABILITY_OPTIONS = AVAILABILITIES.map((a) => ({ value: a, label: AVAILABILITY_LABELS[a] }));

/**
 * Availability has no palette of its own, so every option shares the filter
 * pill. Passed explicitly rather than falling through to PillMultiFilter's
 * default, which is the EOI form's blue outline and should stay that way.
 */
const AVAILABILITY_FILTER_STYLES = Object.fromEntries(
  AVAILABILITIES.map((a) => [a, FILTER_PILL]),
) as Record<Availability, { on: string; off: string }>;

/**
 * The pills in a row double as filter controls, so this is a toggle rather
 * than a plain add: clicking a pill whose value is already being filtered on
 * takes it back out again.
 */
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

/**
 * What an approved company sees before a student applies to them: enough to
 * gauge fit, but no contact details and no CV. Those unlock on application.
 * Skills, hobbies and achievements are included -- see the comment on GET
 * /companies/students for why that's a deliberate, narrow addition rather
 * than forwarding the whole answers column.
 */
export default function StudentDirectory() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["companies", "students"],
    queryFn: () => api.get<{ students: DirectoryStudent[] }>("/companies/students"),
  });

  // Set by "View applicants" on the positions page rather than by a control
  // here, so it lives in the URL: the link is shareable and the back button
  // takes you out of it.
  const [params, setParams] = useSearchParams();
  const positionId = params.get("position");

  const applicants = useQuery({
    queryKey: ["positions", positionId, "applications"],
    queryFn: () => api.get<{ applications: any[] }>(`/positions/${positionId}/applications`),
    enabled: Boolean(positionId),
  });
  // Only for the position's title in the banner -- same key as the positions
  // page, so this is served from cache when arriving via that link.
  const company = useQuery({
    queryKey: ["company", "me"],
    queryFn: () => api.get<{ company: any }>("/companies/me"),
    enabled: Boolean(positionId),
  });

  const [specialisationFilter, setSpecialisationFilter] = useState<Specialisation[]>([]);
  const [yearFilter, setYearFilter] = useState<YearOfStudy[]>([]);
  const [skillFilter, setSkillFilter] = useState<string[]>([]);
  const [availabilityFilter, setAvailabilityFilter] = useState<Availability[]>([]);
  const [hobbySearch, setHobbySearch] = useState("");
  const [achievementSearch, setAchievementSearch] = useState("");
  const [openStudent, setOpenStudent] = useState<DirectoryStudent | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const students = data?.students ?? [];

  /** Applications for the filtered position, keyed by student profile id. */
  const applicationByStudent = useMemo(() => {
    const map = new Map<string, any>();
    for (const a of applicants.data?.applications ?? []) {
      const id = a.student?.studentProfile?.id;
      if (id) map.set(id, a);
    }
    return map;
  }, [applicants.data]);

  const visible = useMemo(() => {
    const hobbyNeedle = hobbySearch.trim().toLowerCase();
    const achievementNeedle = achievementSearch.trim().toLowerCase();
    const filtered = students.filter((s) => {
      // Every applicant has submitted an EOI (the API requires it), so the
      // applicant set is always a subset of these rows.
      if (positionId && !applicationByStudent.has(s.id)) return false;
      if (specialisationFilter.length > 0 && !specialisationFilter.includes(s.specialisation)) return false;
      if (yearFilter.length > 0 && !yearFilter.includes(s.yearOfStudy)) return false;
      if (skillFilter.length > 0 && !s.skills.some((i) => skillFilter.includes(i))) return false;
      if (availabilityFilter.length > 0 && !(s.availability && availabilityFilter.includes(s.availability))) return false;
      if (hobbyNeedle && !s.hobbies.some((h) => h.toLowerCase().includes(hobbyNeedle))) return false;
      if (achievementNeedle && !s.achievements.some((h) => h.toLowerCase().includes(achievementNeedle))) return false;
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => {
      if (sortKey === "name") {
        return dir * `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
      }
      if (sortKey === "specialisation") {
        return dir * (SPECIALISATIONS.indexOf(a.specialisation) - SPECIALISATIONS.indexOf(b.specialisation));
      }
      return dir * (YEARS_OF_STUDY.indexOf(a.yearOfStudy) - YEARS_OF_STUDY.indexOf(b.yearOfStudy));
    });
  }, [students, positionId, applicationByStudent, specialisationFilter, yearFilter, skillFilter,
      availabilityFilter, hobbySearch, achievementSearch, sortKey, sortDir]);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="error">Your organisation is not approved to browse students yet.</Alert>;

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const activeFilters =
    specialisationFilter.length + yearFilter.length + skillFilter.length + availabilityFilter.length +
    (hobbySearch.trim() ? 1 : 0) + (achievementSearch.trim() ? 1 : 0);

  const position = company.data?.company?.positions?.find((p: any) => p.id === positionId);
  const clearPosition = () => {
    const next = new URLSearchParams(params);
    next.delete("position");
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeading
        title="Students seeking internships"
        description="Students who have submitted an expression of interest. Contact details and CVs become available once a student applies to one of your positions."
      />

      {positionId && (
        <Alert>
          Showing only applicants for{" "}
          <strong>{position?.title ?? "this position"}</strong>
          {applicants.isLoading && " (loading...)"}.{" "}
          <button type="button" onClick={clearPosition} className="underline">
            Show all students
          </button>
          {" · "}
          <Link to="/positions" className="underline">Back to positions</Link>
        </Alert>
      )}

      {students.length === 0 ? (
        <Empty>No students have submitted an expression of interest yet.</Empty>
      ) : (
        <>
          <div className="card mb-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <FieldPanel label="Skills" full>
                <SkillPicker value={skillFilter} onChange={setSkillFilter} compact />
              </FieldPanel>
              <FieldPanel label="Specialisation">
                <PillMultiFilter options={SPECIALISATION_OPTIONS} value={specialisationFilter} onChange={setSpecialisationFilter} ariaLabel="Filter by specialisation" styles={SPECIALISATION_STYLES} compact />
              </FieldPanel>
              <FieldPanel label="Year">
                <PillMultiFilter options={YEAR_OPTIONS} value={yearFilter} onChange={setYearFilter} ariaLabel="Filter by year of study" styles={YEAR_STYLES} compact />
              </FieldPanel>
              <FieldPanel label="Availability">
                <PillMultiFilter options={AVAILABILITY_OPTIONS} value={availabilityFilter} onChange={setAvailabilityFilter} ariaLabel="Filter by availability" styles={AVAILABILITY_FILTER_STYLES} compact />
              </FieldPanel>
              <FieldPanel label="Achievements">
                <input
                  type="search"
                  aria-label="Search achievements"
                  className="input"
                  placeholder="e.g. AWS certification"
                  value={achievementSearch}
                  onChange={(e) => setAchievementSearch(e.target.value)}
                />
              </FieldPanel>
              <FieldPanel label="Hobbies">
                <input
                  type="search"
                  aria-label="Search hobbies"
                  className="input"
                  placeholder="e.g. chess"
                  value={hobbySearch}
                  onChange={(e) => setHobbySearch(e.target.value)}
                />
              </FieldPanel>
            </div>
            {activeFilters > 0 && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  className="text-sm font-medium text-uoa-blue underline dark:text-uoa-blue-light"
                  onClick={() => {
                    setSpecialisationFilter([]); setYearFilter([]); setSkillFilter([]);
                    setAvailabilityFilter([]); setHobbySearch(""); setAchievementSearch("");
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {visible.length === 0 ? (
            <Empty>No students match these filters.</Empty>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-line bg-surface">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">Students who have submitted an expression of interest</caption>
                <thead className="bg-uoa-sand">
                  <tr>
                    <SortableHeader label="Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableHeader label="Specialisation" sortKey="specialisation" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableHeader label="Year" sortKey="year" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th scope="col" className="px-4 py-3 font-semibold">Availability</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Skills</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Achievements</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Hobbies</th>
                    <th scope="col" className="px-4 py-3 font-semibold">More info</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => (
                    <tr key={s.id} className="border-t border-line">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setOpenStudent(s)}
                          title={`Read about ${s.firstName}`}
                          className="font-medium text-uoa-blue underline decoration-dotted underline-offset-2 hover:decoration-solid dark:text-uoa-blue-light"
                        >
                          {s.firstName} {s.lastName}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <SpecialisationBadge
                          value={s.specialisation}
                          active={specialisationFilter.includes(s.specialisation)}
                          onClick={() => setSpecialisationFilter((f) => toggle(f, s.specialisation))}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <YearBadge
                          value={s.yearOfStudy}
                          active={yearFilter.includes(s.yearOfStudy)}
                          onClick={() => setYearFilter((f) => toggle(f, s.yearOfStudy))}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {s.availability ? <Badge>{AVAILABILITY_LABELS[s.availability]}</Badge> : null}
                      </td>
                      <td className="px-4 py-3">
                        <SkillTags
                          skills={s.skills}
                          activeSkills={skillFilter}
                          onToggle={(skill) => setSkillFilter((f) => toggle(f, skill))}
                        />
                      </td>
                      <td className="px-4 py-3 text-uoa-grey">{s.achievements.join(", ")}</td>
                      <td className="px-4 py-3 text-uoa-grey">{s.hobbies.join(", ")}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setOpenStudent(s)}
                          title={`Read about ${s.firstName}`}
                          aria-label={`More about ${s.firstName} ${s.lastName}`}
                          className="px-1 font-semibold tracking-widest text-uoa-blue hover:underline dark:text-uoa-blue-light"
                        >
                          ...
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Modal
        open={Boolean(openStudent)}
        onClose={() => setOpenStudent(null)}
        title={openStudent ? `${openStudent.firstName} ${openStudent.lastName}` : ""}
      >
        {openStudent && (
          <AboutPanel
            student={openStudent}
            resumeFileId={
              applicationByStudent.get(openStudent.id)?.student?.studentProfile?.resumeFileId ?? null
            }
          />
        )}
      </Modal>
    </>
  );
}

/**
 * The student's own pitch, with the structured fields as a header for context.
 * The CV is an attachment to this rather than the main event, and only appears
 * when the company is entitled to it -- which is to say when this student has
 * applied to the position the table is scoped to.
 */
function AboutPanel({ student, resumeFileId }: { student: DirectoryStudent; resumeFileId: string | null }) {
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SpecialisationBadge value={student.specialisation} />
        <YearBadge value={student.yearOfStudy} />
        {student.availability && <Badge>{AVAILABILITY_LABELS[student.availability]}</Badge>}
      </div>

      {student.aboutMe ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{student.aboutMe}</p>
      ) : (
        <p className="text-sm text-uoa-grey">This student has not written an About me section yet.</p>
      )}

      {student.skills.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-uoa-grey">Skills</p>
          <SkillTags skills={student.skills} />
        </div>
      )}
      {student.achievements.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-uoa-grey">Achievements</p>
          <p className="text-sm">{student.achievements.join(", ")}</p>
        </div>
      )}
      {student.hobbies.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-uoa-grey">Hobbies</p>
          <p className="text-sm">{student.hobbies.join(", ")}</p>
        </div>
      )}

      <div className="mt-5 border-t border-line pt-4 text-sm">
        {resumeFileId ? (
          <a
            className="text-uoa-blue underline dark:text-uoa-blue-light"
            target="_blank"
            rel="noreferrer"
            href={`/api/files/${resumeFileId}`}
          >
            Open CV (PDF)
          </a>
        ) : (
          <span className="text-uoa-grey">
            The CV becomes available once this student applies to one of your positions.
          </span>
        )}
      </div>
    </>
  );
}

function SortableHeader({
  label, sortKey, activeKey, dir, onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  const active = sortKey === activeKey;
  return (
    <th scope="col" aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"} className="px-4 py-3 font-semibold">
      <button type="button" onClick={() => onSort(sortKey)} className="inline-flex items-center gap-1 hover:text-uoa-blue dark:hover:text-uoa-blue-light">
        {label}
        <span aria-hidden className={active ? "text-uoa-blue dark:text-uoa-blue-light" : "text-uoa-grey/50"}>
          {active ? (dir === "asc" ? "▲" : "▼") : "▲"}
        </span>
      </button>
    </th>
  );
}
